import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SignaturePad } from "./SignaturePad";
import {
  FileText,
  User,
  Car,
  CreditCard,
  PenTool,
  Download,
  Check,
  Loader2,
  FileCheck,
  Languages,
  Printer
} from "lucide-react";
import api from "@/lib/api";

export const SalesCenter = ({ scooter, onSaleComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [documents, setDocuments] = useState([]);
  
  // Seller info (from scooter or manual)
  const [seller, setSeller] = useState({
    name: scooter?.seller_info?.name || "",
    address: scooter?.seller_info?.address || "",
    id_number: scooter?.seller_info?.id_number || "",
    phone: scooter?.seller_info?.phone || ""
  });
  
  // Buyer info
  const [buyer, setBuyer] = useState({
    name: "",
    address: "",
    id_number: "",
    phone: "",
    email: ""
  });
  
  // Sale details
  const [saleDetails, setSaleDetails] = useState({
    sale_price: scooter?.selling_price || 0,
    payment_method: "cash",
    document_type: "rar",
    rar_assistance: false,
    warranty: "",
    location: "",
    language_pair: "fr_ro",
    notes: ""
  });
  
  // Signatures
  const [sellerSignature, setSellerSignature] = useState("");
  const [buyerSignature, setBuyerSignature] = useState("");
  const [showSellerSignature, setShowSellerSignature] = useState(false);
  const [showBuyerSignature, setShowBuyerSignature] = useState(false);
  
  // Existing clients for autocomplete
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  
  useEffect(() => {
    loadClients();
  }, []);
  
  const loadClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };
  
  const selectExistingClient = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    if (client) {
      setSelectedClient(client);
      setBuyer({
        name: client.name,
        address: client.address,
        id_number: client.id_number,
        phone: client.phone,
        email: client.email
      });
    }
  };
  
  const completeSale = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/scooters/${scooter.scooter_id}/complete-sale`, {
        seller,
        buyer,
        sale_price: saleDetails.sale_price,
        payment_method: saleDetails.payment_method,
        document_type: saleDetails.document_type,
        rar_assistance: saleDetails.rar_assistance,
        warranty: saleDetails.warranty,
        location: saleDetails.location,
        language_pair: saleDetails.language_pair,
        seller_signature: sellerSignature,
        buyer_signature: buyerSignature,
        save_client: true,
        notes: saleDetails.notes
      });
      
      setDocuments(response.data.documents);
      setSaleComplete(true);
      toast.success("Vente finalisée avec succès!");
      
      if (onSaleComplete) {
        onSaleComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vente");
    } finally {
      setLoading(false);
    }
  };
  
  const downloadDocument = async (docType) => {
    try {
      const response = await api.get(`/scooters/${scooter.scooter_id}/sale-documents/${docType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}_${scooter.scooter_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  const printDocument = async (docType) => {
    try {
      const response = await api.get(`/scooters/${scooter.scooter_id}/sale-documents/${docType}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error("Erreur lors de l'impression");
    }
  };
  
  const printAllDocuments = async () => {
    const docTypes = ['sales_contract', 'warranty_certificate', 'technical_report', 'payment_receipt'];
    for (const docType of docTypes) {
      await printDocument(docType);
      // Small delay between prints
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };
  
  const regenerateDocuments = async (newLanguage) => {
    setLoading(true);
    try {
      const response = await api.post(`/scooters/${scooter.scooter_id}/regenerate-documents?language_pair=${newLanguage}`);
      setDocuments(response.data.documents);
      setSaleDetails(prev => ({ ...prev, language_pair: newLanguage }));
      toast.success("Documents régénérés!");
    } catch (error) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setLoading(false);
    }
  };
  
  // If sale is already complete, show documents
  if (scooter?.sale_record?.documents?.length > 0 || saleComplete) {
    const docs = saleComplete ? documents : scooter.sale_record.documents;
    const saleRecord = scooter?.sale_record;
    
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileCheck className="w-5 h-5 text-primary" />
                Documents de Vente
              </CardTitle>
              <CardDescription>
                Vente finalisée le {saleRecord?.sale_date} à {saleRecord?.sale_time}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={printAllDocuments}
                className="text-primary border-primary/30"
                data-testid="btn-print-all"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer tout
              </Button>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                Vendu
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Buyer Info */}
          {saleRecord && (
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Acheteur</h4>
              <p className="text-sm text-muted-foreground">{saleRecord.buyer_name}</p>
              <p className="text-sm text-muted-foreground">{saleRecord.buyer_phone}</p>
              {saleRecord.buyer_email && (
                <p className="text-sm text-muted-foreground">{saleRecord.buyer_email}</p>
              )}
            </div>
          )}
          
          {/* Documents */}
          <div className="grid grid-cols-2 gap-3">
            {docs.map((doc, index) => (
              <div key={index} className="flex gap-1">
                <Button
                  variant="outline"
                  className="flex-1 justify-start h-auto py-3"
                  onClick={() => downloadDocument(doc.doc_type)}
                  data-testid={`btn-download-${doc.doc_type}`}
                >
                  <Download className="w-4 h-4 mr-2 text-primary" />
                  <div className="text-left">
                    <p className="text-sm font-medium">
                      {doc.doc_type === 'sales_contract' && 'Contrat de Vente'}
                      {doc.doc_type === 'warranty_certificate' && 'Certificat Garantie'}
                      {doc.doc_type === 'technical_report' && 'Fiche Technique'}
                      {doc.doc_type === 'payment_receipt' && 'Quittance'}
                    </p>
                    <p className="text-xs text-muted-foreground">PDF</p>
                  </div>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => printDocument(doc.doc_type)}
                  className="h-auto"
                  data-testid={`btn-print-${doc.doc_type}`}
                >
                  <Printer className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          
          {/* Regenerate in different language */}
          <div className="flex items-center gap-2 pt-4 border-t border-border">
            <Languages className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Régénérer en:</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => regenerateDocuments('fr_ro')}
              disabled={loading}
              className={saleDetails.language_pair === 'fr_ro' ? 'bg-primary/20' : ''}
            >
              FR/RO
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => regenerateDocuments('nl_ro')}
              disabled={loading}
              className={saleDetails.language_pair === 'nl_ro' ? 'bg-primary/20' : ''}
            >
              NL/RO
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="w-5 h-5 text-primary" />
          Centre de Vente
        </CardTitle>
        <CardDescription>
          Finalisez la vente et générez tous les documents légaux
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step >= s ? 'bg-primary text-black' : 'bg-muted text-muted-foreground'}`}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-0.5 mx-1 ${step > s ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Step 1: Seller Info */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary" />
              <h3 className="font-medium">Informations Vendeur</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="seller-name">Nom complet</Label>
                <Input
                  id="seller-name"
                  value={seller.name}
                  onChange={(e) => setSeller({ ...seller, name: e.target.value })}
                  placeholder="Nom du vendeur"
                  data-testid="input-seller-name"
                />
              </div>
              <div>
                <Label htmlFor="seller-phone">Téléphone</Label>
                <Input
                  id="seller-phone"
                  value={seller.phone}
                  onChange={(e) => setSeller({ ...seller, phone: e.target.value })}
                  placeholder="Numéro de téléphone"
                  data-testid="input-seller-phone"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="seller-address">Adresse</Label>
                <Input
                  id="seller-address"
                  value={seller.address}
                  onChange={(e) => setSeller({ ...seller, address: e.target.value })}
                  placeholder="Adresse complète"
                  data-testid="input-seller-address"
                />
              </div>
              <div>
                <Label htmlFor="seller-id">N° Identification</Label>
                <Input
                  id="seller-id"
                  value={seller.id_number}
                  onChange={(e) => setSeller({ ...seller, id_number: e.target.value })}
                  placeholder="N° CI / Passeport"
                  data-testid="input-seller-id"
                />
              </div>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setStep(2)}
                disabled={!seller.name}
                className="bg-primary text-black"
                data-testid="btn-next-step-1"
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 2: Buyer Info */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary" />
              <h3 className="font-medium">Informations Acheteur</h3>
            </div>
            
            {/* Existing client selector */}
            {clients.length > 0 && (
              <div>
                <Label>Client existant</Label>
                <Select onValueChange={selectExistingClient}>
                  <SelectTrigger data-testid="select-existing-client">
                    <SelectValue placeholder="Sélectionner un client existant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name} - {client.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="buyer-name">Nom complet *</Label>
                <Input
                  id="buyer-name"
                  value={buyer.name}
                  onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                  placeholder="Nom de l'acheteur"
                  data-testid="input-buyer-name"
                />
              </div>
              <div>
                <Label htmlFor="buyer-phone">Téléphone *</Label>
                <Input
                  id="buyer-phone"
                  value={buyer.phone}
                  onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                  placeholder="Numéro de téléphone"
                  data-testid="input-buyer-phone"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="buyer-address">Adresse</Label>
                <Input
                  id="buyer-address"
                  value={buyer.address}
                  onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
                  placeholder="Adresse complète"
                  data-testid="input-buyer-address"
                />
              </div>
              <div>
                <Label htmlFor="buyer-id">N° Identification</Label>
                <Input
                  id="buyer-id"
                  value={buyer.id_number}
                  onChange={(e) => setBuyer({ ...buyer, id_number: e.target.value })}
                  placeholder="N° CI / Passeport"
                  data-testid="input-buyer-id"
                />
              </div>
              <div>
                <Label htmlFor="buyer-email">Email</Label>
                <Input
                  id="buyer-email"
                  type="email"
                  value={buyer.email}
                  onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                  placeholder="Email (optionnel)"
                  data-testid="input-buyer-email"
                />
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!buyer.name || !buyer.phone}
                className="bg-primary text-black"
                data-testid="btn-next-step-2"
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Sale Details */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-primary" />
              <h3 className="font-medium">Détails de la Vente</h3>
            </div>
            
            {/* Vehicle summary */}
            <div className="bg-muted/30 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{scooter.brand} {scooter.model} ({scooter.year})</span>
              </div>
              <p className="text-sm text-muted-foreground">VIN: {scooter.vin}</p>
              <p className="text-sm text-muted-foreground">KM: {scooter.mileage?.toLocaleString()}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sale-price">Prix de vente (EUR) *</Label>
                <Input
                  id="sale-price"
                  type="number"
                  value={saleDetails.sale_price}
                  onChange={(e) => setSaleDetails({ ...saleDetails, sale_price: parseFloat(e.target.value) })}
                  data-testid="input-sale-price"
                />
              </div>
              <div>
                <Label>Mode de paiement</Label>
                <Select
                  value={saleDetails.payment_method}
                  onValueChange={(v) => setSaleDetails({ ...saleDetails, payment_method: v })}
                >
                  <SelectTrigger data-testid="select-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="transfer">Virement</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Documents véhicule</Label>
                <Select
                  value={saleDetails.document_type}
                  onValueChange={(v) => setSaleDetails({ ...saleDetails, document_type: v })}
                >
                  <SelectTrigger data-testid="select-document-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rar">Carte RAR Roumanie</SelectItem>
                    <SelectItem value="foreign">Carte étrangère</SelectItem>
                    <SelectItem value="none">Sans documents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Langue des documents</Label>
                <Select
                  value={saleDetails.language_pair}
                  onValueChange={(v) => setSaleDetails({ ...saleDetails, language_pair: v })}
                >
                  <SelectTrigger data-testid="select-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr_ro">Français / Roumain</SelectItem>
                    <SelectItem value="nl_ro">Néerlandais / Roumain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="warranty">Garantie (laisser vide si aucune)</Label>
                <Input
                  id="warranty"
                  value={saleDetails.warranty}
                  onChange={(e) => setSaleDetails({ ...saleDetails, warranty: e.target.value })}
                  placeholder="Ex: 3 mois / 3000 km"
                  data-testid="input-warranty"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="location">Lieu de vente</Label>
                <Input
                  id="location"
                  value={saleDetails.location}
                  onChange={(e) => setSaleDetails({ ...saleDetails, location: e.target.value })}
                  placeholder="Ville - Pays"
                  data-testid="input-location"
                />
              </div>
              <div className="col-span-2 flex items-center space-x-2">
                <Checkbox
                  id="rar-assist"
                  checked={saleDetails.rar_assistance}
                  onCheckedChange={(checked) => setSaleDetails({ ...saleDetails, rar_assistance: checked })}
                  data-testid="checkbox-rar-assistance"
                />
                <Label htmlFor="rar-assist" className="cursor-pointer">
                  Assistance RAR Roumanie incluse
                </Label>
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={saleDetails.notes}
                  onChange={(e) => setSaleDetails({ ...saleDetails, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                Retour
              </Button>
              <Button
                onClick={() => setStep(4)}
                disabled={!saleDetails.sale_price}
                className="bg-primary text-black"
                data-testid="btn-next-step-3"
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 4: Signatures */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <PenTool className="w-5 h-5 text-primary" />
              <h3 className="font-medium">Signatures</h3>
            </div>
            
            {/* Summary */}
            <div className="bg-muted/30 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Véhicule:</span>
                <span className="font-medium">{scooter.brand} {scooter.model}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Prix:</span>
                <span className="font-medium text-primary">{saleDetails.sale_price} EUR</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vendeur:</span>
                <span>{seller.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Acheteur:</span>
                <span>{buyer.name}</span>
              </div>
            </div>
            
            {/* Signature buttons */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Dialog open={showSellerSignature} onOpenChange={setShowSellerSignature}>
                  <DialogTrigger asChild>
                    <Button
                      variant={sellerSignature ? "default" : "outline"}
                      className={`w-full h-20 ${sellerSignature ? 'bg-green-500/20 border-green-500/30' : ''}`}
                      data-testid="btn-seller-signature"
                    >
                      <div className="text-center">
                        {sellerSignature ? (
                          <>
                            <Check className="w-6 h-6 mx-auto mb-1 text-green-500" />
                            <span className="text-sm">Vendeur signé</span>
                          </>
                        ) : (
                          <>
                            <PenTool className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-sm">Signature Vendeur</span>
                          </>
                        )}
                      </div>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Signature du Vendeur</DialogTitle>
                    </DialogHeader>
                    <SignaturePad
                      title={seller.name}
                      onSave={(sig) => {
                        setSellerSignature(sig);
                        setShowSellerSignature(false);
                      }}
                      onCancel={() => setShowSellerSignature(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
              
              <div>
                <Dialog open={showBuyerSignature} onOpenChange={setShowBuyerSignature}>
                  <DialogTrigger asChild>
                    <Button
                      variant={buyerSignature ? "default" : "outline"}
                      className={`w-full h-20 ${buyerSignature ? 'bg-green-500/20 border-green-500/30' : ''}`}
                      data-testid="btn-buyer-signature"
                    >
                      <div className="text-center">
                        {buyerSignature ? (
                          <>
                            <Check className="w-6 h-6 mx-auto mb-1 text-green-500" />
                            <span className="text-sm">Acheteur signé</span>
                          </>
                        ) : (
                          <>
                            <PenTool className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-sm">Signature Acheteur</span>
                          </>
                        )}
                      </div>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Signature de l'Acheteur</DialogTitle>
                    </DialogHeader>
                    <SignaturePad
                      title={buyer.name}
                      onSave={(sig) => {
                        setBuyerSignature(sig);
                        setShowBuyerSignature(false);
                      }}
                      onCancel={() => setShowBuyerSignature(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                Retour
              </Button>
              <Button
                onClick={completeSale}
                disabled={loading || !sellerSignature || !buyerSignature}
                className="bg-primary text-black"
                data-testid="btn-complete-sale"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Finaliser la Vente
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesCenter;
